// The A/B comparison dialog. Matches the desktop ABTestingDialog: a tall window
// (~1200x840 there), the prompt on top, "Option A" / "Option B" titles over two
// scrollable mini-chat panes, a select button under each, and a status line.
// Selection is locked until BOTH sides finish revealing (the panes are timed by
// abRevealTimings to reveal together), and latency is measured from the moment
// selection unlocks.

import { useEffect, useRef, useState } from "react";
import AbTestPane from "./AbTestPane";
import { abRevealTimings, type AbPaneRaw } from "./presentation";

export default function AbTestDialog({
  promptMessage,
  optionA,
  optionB,
  rawA,
  rawB,
  defaultRevealDelayMs,
  onSelect,
}: {
  promptMessage: string;
  optionA: string;
  optionB: string;
  rawA: AbPaneRaw;
  rawB: AbPaneRaw;
  defaultRevealDelayMs?: number | undefined;
  onSelect: (selection: "A" | "B", latencyMs: number) => void;
}) {
  const [aReady, setAReady] = useState(false);
  const [bReady, setBReady] = useState(false);
  const enabledTsRef = useRef<number | null>(null);
  const both = aReady && bReady;

  useEffect(() => {
    if (both && enabledTsRef.current === null) enabledTsRef.current = Date.now();
  }, [both]);

  const { blankA, blankB } = abRevealTimings(rawA, rawB, defaultRevealDelayMs);

  const pick = (side: "A" | "B") => {
    if (!both) return;
    const latency = enabledTsRef.current !== null ? Date.now() - enabledTsRef.current : 0;
    onSelect(side, latency);
  };

  const renderPanel = (
    side: "A" | "B",
    text: string,
    raw: AbPaneRaw,
    blankMs: number,
    setReady: (v: boolean) => void,
  ) => (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ fontWeight: 600, textAlign: "center" }}>Option {side}</div>
      <div
        className="jol-ab-pane-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          border: "1px solid var(--jol-border, #e2e6ea)",
          borderRadius: 10,
          padding: 12,
          background: "#f7f9fb",
          minHeight: 0,
        }}
      >
        <AbTestPane text={text} raw={raw} blankMs={blankMs} onReady={() => setReady(true)} />
      </div>
      <button className="jol-btn" disabled={!both} onClick={() => pick(side)}>
        Choose Option {side}
      </button>
    </div>
  );

  return (
    <div
      className="jol-ab-overlay"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 60,
        background: "rgba(0, 0, 0, 0.18)",
      }}
    >
      <div
        className="jol-ab-dialog"
        style={{
          width: "min(1180px, 96vw)",
          height: "min(840px, 92vh)",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          border: "1px solid var(--jol-border, #d8dee4)",
          borderRadius: 12,
          boxShadow: "0 8px 40px rgba(0, 0, 0, 0.25)",
          padding: 20,
        }}
      >
        {promptMessage ? (
          <div
            className="jol-ab-prompt"
            style={{ fontWeight: 600, textAlign: "center", marginBottom: 12 }}
          >
            {promptMessage}
          </div>
        ) : null}
        <div className="jol-ab-panes" style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
          {renderPanel("A", optionA, rawA, blankA, setAReady)}
          {renderPanel("B", optionB, rawB, blankB, setBReady)}
        </div>
        <div
          className="jol-ab-status"
          style={{ textAlign: "center", marginTop: 12, color: "#6b7680" }}
        >
          {both ? "Make your choice!" : "Loading responses..."}
        </div>
      </div>
    </div>
  );
}
